import { ip_back } from "../config";
import { captureVideoFrameJpeg } from "./videoContainCoords";

/** videoUrl → object URL for a same-origin copy (never swap the playing <video>). */
const blobUrlCache = new Map();

function authHeader() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token ? { Authorization: token } : {};
}

/** Drop a presigned query so the same object shares one downloaded copy. */
function canonicalSourceUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    if (
      host === "s3.gra.io.cloud.ovh.net" ||
      host.endsWith(".s3.gra.io.cloud.ovh.net")
    ) {
      parsed.search = "";
      parsed.hash = "";
      return parsed.toString();
    }
  } catch {
    /* keep the raw string */
  }
  return raw.split("#")[0];
}

async function postRemoteFileBlob(sourceUrl, signal, minBytes = 1) {
  const url = canonicalSourceUrl(sourceUrl);
  if (!url || !/^https?:\/\//i.test(url)) return null;
  let resp;
  try {
    resp = await fetch(`${ip_back}api/mobileAPI/pose/video-file`, {
      method: "POST",
      headers: { ...authHeader(), "Content-Type": "application/json" },
      signal,
      body: JSON.stringify({ url }),
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
  if (!resp.ok) return null;
  const type = String(resp.headers.get("content-type") || "");
  if (type.includes("application/json")) return null;
  let blob;
  try {
    blob = await resp.blob();
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
  if (!blob || blob.size < minBytes) return null;
  return URL.createObjectURL(blob);
}

function waitForEvent(el, eventName, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onErr = () => {
      cleanup();
      reject(new Error(`Video ${eventName} failed`));
    };
    const cleanup = () => {
      el.removeEventListener(eventName, onOk);
      el.removeEventListener("error", onErr);
      signal?.removeEventListener("abort", onAbort);
    };
    el.addEventListener(eventName, onOk, { once: true });
    el.addEventListener("error", onErr, { once: true });
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Fetch the exact video URL through the API (same-origin blob) so canvas
 * capture works. Must use the playing clip's URL — not "latest for te_id".
 */
export async function ensurePoseVideoBlobUrl(videoUrl, signal) {
  const sourceUrl = canonicalSourceUrl(videoUrl);
  if (!sourceUrl) return null;
  const cached = blobUrlCache.get(sourceUrl);
  if (cached) return cached;

  const objectUrl = await postRemoteFileBlob(sourceUrl, signal, 100);
  if (!objectUrl) {
    throw new Error("Could not download video for frame capture");
  }
  blobUrlCache.set(sourceUrl, objectUrl);
  return objectUrl;
}

/**
 * Same-origin blob for remote images (e.g. club logos on S3) so canvas export
 * is not blocked by CORS. Caller must revoke the returned object URL.
 */
export async function fetchProxiedRemoteBlobUrl(remoteUrl, signal) {
  return postRemoteFileBlob(remoteUrl, signal, 1);
}

/**
 * Capture JPEG of the frame the coach is looking at.
 * 1) Try the live <video> (works for same-origin sources).
 * 2) Else decode a same-origin blob of the *same* source URL offscreen.
 * Never replaces the visible player source.
 */
export async function capturePausedVideoJpeg(videoEl, videoUrl, signal, options = {}) {
  const maxEdge = Number(options.maxEdge) || 0;
  const quality = Number(options.quality) > 0 ? Number(options.quality) : 0.82;
  const direct = captureVideoFrameJpeg(videoEl, quality, maxEdge);
  if (direct) return direct;
  // Joint overlay on the GCS app must not download the whole clip through
  // the API — that cross-origin transfer fails as "Failed to fetch".
  if (options.proxy === false) return null;

  const sourceUrl =
    String(videoUrl || "").trim() ||
    String(videoEl?.currentSrc || videoEl?.src || "").trim();
  if (!sourceUrl || sourceUrl.startsWith("blob:")) return null;

  const blobUrl = await ensurePoseVideoBlobUrl(sourceUrl, signal);
  if (!blobUrl) return null;

  const v = document.createElement("video");
  v.muted = true;
  v.playsInline = true;
  v.preload = "auto";
  v.src = blobUrl;

  try {
    if (v.readyState < 2) {
      await waitForEvent(v, "loadeddata", signal);
    }
    const t =
      videoEl && Number.isFinite(videoEl.currentTime) ? videoEl.currentTime : 0;
    if (Math.abs((v.currentTime || 0) - t) > 0.0005) {
      const seeked = waitForEvent(v, "seeked", signal);
      v.currentTime = Math.max(0, t);
      await seeked;
    }
    await new Promise((r) => requestAnimationFrame(() => r()));
    return captureVideoFrameJpeg(v);
  } finally {
    v.removeAttribute("src");
    try {
      v.load();
    } catch {
      /* ignore */
    }
  }
}

export function revokePoseVideoBlobUrl(videoUrl) {
  const key = canonicalSourceUrl(videoUrl);
  if (!key) return;
  const objectUrl = blobUrlCache.get(key);
  if (!objectUrl) return;
  blobUrlCache.delete(key);
  try {
    URL.revokeObjectURL(objectUrl);
  } catch {
    /* ignore */
  }
}
